// =============================================================================
// The "new shared calendar" modal: a name, an optional birthday list, and
// the create call. Its draft state lives in Calendar.js (the header button
// clears the name before opening), so this only renders it.
// =============================================================================

import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { createSharedCalendar } from '../../lib/sharedCalendars';

const CreateSharedCalendarModal = ({
  newSharedName,
  setNewSharedName,
  newSharedWithBirthdays,
  setNewSharedWithBirthdays,
  creatingShared,
  setCreatingShared,
  onClose,
  reload
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('sharedCalendar.createTitle')}
          </h3>
          <button
            onClick={() => onClose()}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>
        <input
          type="text"
          autoFocus
          className="input w-full"
          placeholder={t('sharedCalendar.namePlaceholder')}
          value={newSharedName}
          onChange={(e) => setNewSharedName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newSharedName.trim() && !creatingShared) {
              e.preventDefault();
              (async () => {
                setCreatingShared(true);
                try {
                  await createSharedCalendar(newSharedName.trim(), {
                    withBirthdays: newSharedWithBirthdays
                  });
                  if (reload) await reload();
                  onClose();
                  setNewSharedName('');
                  setNewSharedWithBirthdays(false);
                } catch (err) {
                  window.alert(err.message || t('sharedCalendar.createFailed'));
                } finally {
                  setCreatingShared(false);
                }
              })();
            }
          }}
        />
        <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={newSharedWithBirthdays}
            onChange={(e) => setNewSharedWithBirthdays(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary-600"
          />
          <span className="text-sm">
            <span className="font-medium text-gray-800 dark:text-gray-100">
              {t('sharedCalendar.withBirthdays')}
            </span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {t('sharedCalendar.withBirthdaysHint')}
            </span>
          </span>
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => onClose()}
            className="btn-secondary"
            disabled={creatingShared}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              if (!newSharedName.trim()) return;
              setCreatingShared(true);
              try {
                await createSharedCalendar(newSharedName.trim(), {
                  withBirthdays: newSharedWithBirthdays
                });
                if (reload) await reload();
                onClose();
                setNewSharedName('');
                setNewSharedWithBirthdays(false);
              } catch (err) {
                window.alert(err.message || t('sharedCalendar.createFailed'));
              } finally {
                setCreatingShared(false);
              }
            }}
            disabled={creatingShared || !newSharedName.trim()}
            className="btn bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {t('sharedCalendar.create')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSharedCalendarModal;
